import React, { ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { StatsigClient } from '@statsig/js-client';
import { logEvent as logFirebaseEvent } from 'firebase/analytics';
import { analytics, auth } from '../firebase';
import { useProject } from '../contexts/ProjectContext';

type AnalyticsValue = string | number | boolean | null | undefined;
export type AnalyticsMetadata = Record<string, AnalyticsValue>;

interface TrackEventOptions {
  dedupeKey?: string;
  dedupeMs?: number;
}

interface AnalyticsApi {
  trackEvent: typeof trackEvent;
  trackPageView: typeof trackPageView;
}

interface AnalyticsAuthUser {
  uid?: string | null;
  email?: string | null;
}

const STATSIG_CLIENT_KEY: string = process.env.REACT_APP_STATSIG_CLIENT_KEY?.trim() ?? '';
const DEFAULT_DEDUPE_MS = 1500;

let statsigClient: StatsigClient | null = null;
let statsigClientPromise: Promise<StatsigClient | null> | null = null;
let lastStatsigUserFingerprint: string | null = null;
let currentAnalyticsUser: AnalyticsAuthUser | null = null;
let analyticsBaseContext: AnalyticsMetadata = {};

const recentEvents: Map<string, number> = new Map<string, number>();
const AnalyticsContext = createContext<AnalyticsApi | undefined>(undefined);

function sanitizeEventName(eventName: string): string {
  const sanitized: string = eventName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return sanitized || 'custom_event';
}

function sanitizeMetadataValue(value: AnalyticsValue): string | number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  if (value === null) {
    return 'null';
  }

  return String(value).slice(0, 100);
}

function normalizeMetadata(metadata: AnalyticsMetadata): Record<string, string | number> {
  return Object.entries(metadata).reduce<Record<string, string | number>>((acc, [key, value]) => {
    if (value === undefined) {
      return acc;
    }

    const sanitizedKey: string = key
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40);

    if (!sanitizedKey) {
      return acc;
    }

    acc[sanitizedKey] = sanitizeMetadataValue(value);
    return acc;
  }, {});
}

function normalizeStatsigMetadata(metadata: Record<string, string | number>): Record<string, string> {
  return Object.entries(metadata).reduce<Record<string, string>>((acc, [key, value]) => {
    acc[key] = String(value);
    return acc;
  }, {});
}

function buildStatsigUser(currentUser: AnalyticsAuthUser | null, projectId?: string): Record<string, unknown> {
  const email: string | undefined = currentUser?.email?.toLowerCase() ?? undefined;
  const userID: string = String(currentUser?.uid ?? email ?? 'anonymous');

  return {
    userID,
    email,
    customIDs: {
      firebase_uid: currentUser?.uid ?? undefined,
      project_id: projectId,
    },
    custom: {
      project_id: projectId,
      environment: process.env.NODE_ENV ?? 'development',
    },
  };
}

function getStatsigUserFingerprint(currentUser: AnalyticsAuthUser | null, projectId?: string): string {
  return JSON.stringify(buildStatsigUser(currentUser, projectId));
}

async function syncStatsigUser(currentUser: AnalyticsAuthUser | null, projectId?: string): Promise<StatsigClient | null> {
  if (!STATSIG_CLIENT_KEY || typeof window === 'undefined') {
    return null;
  }

  const nextFingerprint: string = getStatsigUserFingerprint(currentUser, projectId);

  if (!statsigClient) {
    const client = new StatsigClient(
      STATSIG_CLIENT_KEY,
      buildStatsigUser(currentUser, projectId),
      {
        environment: { tier: process.env.NODE_ENV ?? 'development' },
      }
    );

    statsigClient = client;
    lastStatsigUserFingerprint = nextFingerprint;
    statsigClientPromise = client
      .initializeAsync()
      .then(() => client)
      .catch((error: unknown) => {
        console.warn('Statsig initialization failed:', error);
        return client;
      });

    return statsigClientPromise;
  }

  if (lastStatsigUserFingerprint !== nextFingerprint) {
    lastStatsigUserFingerprint = nextFingerprint;
    statsigClientPromise = statsigClient
      .updateUserAsync(buildStatsigUser(currentUser, projectId))
      .then(() => statsigClient)
      .catch((error: unknown) => {
        console.warn('Statsig user update failed:', error);
        return statsigClient;
      });
  }

  return statsigClientPromise ?? Promise.resolve(statsigClient);
}

export function trackEvent(
  eventName: string,
  metadata: AnalyticsMetadata = {},
  options: TrackEventOptions = {}
): void {
  const normalizedEventName: string = sanitizeEventName(eventName);
  const normalizedMetadata: Record<string, string | number> = normalizeMetadata({
    ...analyticsBaseContext,
    ...metadata,
    path: analyticsBaseContext.path ?? (typeof window !== 'undefined' ? window.location.pathname : undefined),
  });

  const dedupeMs: number = options.dedupeMs ?? DEFAULT_DEDUPE_MS;
  if (options.dedupeKey) {
    const cacheKey: string = `${normalizedEventName}:${options.dedupeKey}`;
    const lastTrackedAt: number | undefined = recentEvents.get(cacheKey);
    const now: number = Date.now();
    if (lastTrackedAt !== undefined && now - lastTrackedAt < dedupeMs) {
      return;
    }
    recentEvents.set(cacheKey, now);
  }

  try {
    logFirebaseEvent(analytics, normalizedEventName, normalizedMetadata);
  } catch (error) {
    console.warn(`Firebase analytics log failed for ${normalizedEventName}:`, error);
  }

  const projectId: string | undefined =
    typeof analyticsBaseContext.project_id === 'string'
      ? analyticsBaseContext.project_id
      : undefined;

  void (statsigClientPromise ?? syncStatsigUser(currentAnalyticsUser, projectId))
    .then((client: StatsigClient | null) => {
      if (!client) {
        return;
      }
      client.logEvent(normalizedEventName, undefined, normalizeStatsigMetadata(normalizedMetadata));
    })
    .catch((error: unknown) => {
      console.warn(`Statsig log failed for ${normalizedEventName}:`, error);
    });
}

export function trackPageView(pageName: string, metadata: AnalyticsMetadata = {}): void {
  trackEvent('page_view', {
    page_name: pageName,
    ...metadata,
  });
}

export function AnalyticsProvider({ children }: { children: ReactNode }): React.ReactElement {
  const { selectedProject } = useProject();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState<AnalyticsAuthUser | null>(() => (
    auth.currentUser
      ? { uid: auth.currentUser.uid, email: auth.currentUser.email }
      : null
  ));

  useEffect(() => {
    return auth.onAuthStateChanged((user: any) => {
      setCurrentUser(user ? { uid: user.uid, email: user.email } : null);
    });
  }, []);

  useEffect(() => {
    currentAnalyticsUser = currentUser;
    analyticsBaseContext = {
      project_id: selectedProject?.id,
      project_name: selectedProject?.name,
      path: location.pathname,
    };

    void syncStatsigUser(currentUser, selectedProject?.id);
  }, [currentUser, location.pathname, selectedProject?.id, selectedProject?.name]);

  const value = useMemo<AnalyticsApi>(() => ({
    trackEvent,
    trackPageView,
  }), []);

  return React.createElement(AnalyticsContext.Provider, { value }, children);
}

export function useAnalytics(): AnalyticsApi {
  return useContext(AnalyticsContext) ?? { trackEvent, trackPageView };
}
